import ToolTips from "utils/toolTips";
import HiddenToolTip from "utils/hiddenToolTips";
import TagsInput from "react-tagsinput";
import { type ReactNode, useState } from "react";
import { Grid, IconButton } from "@material-ui/core";
import { MdEdit } from "react-icons/md";
import { ReactTags, type Tag } from "react-tag-autocomplete";
import { Button } from "../basic/Button";
import { Modal } from "../basic/RACModal";
import { css } from "../../../../styled-system/css";
import { TextAreaField } from "../../fields/base/TextAreaField";
import { VStack, Wrap } from "../../../../styled-system/jsx";

interface Props {
  name: ReactNode;
  modalTitle?: string;
  toolTip?: string;
  toolTipId?: string;
  toolTipPosition?: "top" | "bottom" | "left" | "right";
  hiddenTooltip?: boolean;
  placeholder?: string;
  disabled?: boolean;
  inputDisabled?: boolean;
  filledTags?: string[];
  saveFilledTags: (tags: string[]) => void;
  hideTagsField?: boolean;
  reviewDisplay?: boolean;
  smallIcon?: boolean;
  hideEditButton?: boolean;
  tagClass?: string;
  overrideButtonStyles?: Record<string, unknown>;
  altButton?: JSX.Element;
  altButtonContainerClasses?: string;
  showSuggestedTags?: boolean;
  suggestedTags?: string[];
  allowNew?: boolean;
  showSuggestionsOnFocus?: boolean;
  fullWidth?: boolean;
  readOnly?: boolean;
}

const Tags = ({
  name,
  modalTitle,
  toolTip,
  toolTipId,
  toolTipPosition,
  hiddenTooltip,
  placeholder,
  disabled,
  inputDisabled = false,
  filledTags = [],
  saveFilledTags,
  hideTagsField,
  reviewDisplay,
  smallIcon,
  hideEditButton,
  tagClass,
  overrideButtonStyles,
  altButton,
  altButtonContainerClasses = "",
  showSuggestedTags = false,
  suggestedTags,
  allowNew = true,
  showSuggestionsOnFocus = false,
  fullWidth = false,
  readOnly = false,
}: Props) => {
  const [showEdit, setShowEdit] = useState(false);
  const [editTags, setEditTags] = useState("");
  const showEditModal = () => {
    setEditTags(filledTags.join("\n"));
    setShowEdit(true);
  };
  const closeEditModal = () => {
    setShowEdit(false);
    setEditTags("");
  };
  const saveEditTags = () => {
    saveFilledTags(
      editTags
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => !!s),
    );
    closeEditModal();
  };

  const suggestions = suggestedTags?.map((tag) => ({ value: tag, label: tag }));

  const onDeleteTag = (tagIndex: number) => {
    saveFilledTags(filledTags.filter((_, i) => i !== tagIndex));
  };

  const onAddTag = (newTag: Tag) => {
    saveFilledTags([...filledTags, newTag.label]);
  };

  return reviewDisplay && filledTags.length < 1 ?
      null
    : <div>
        {!hideTagsField && name}
        {toolTip && !hiddenTooltip && (
          <ToolTips toolTip={toolTip} id={toolTipId} position={toolTipPosition ? toolTipPosition : "top"} />
        )}
        {reviewDisplay ?
          <Grid
            style={{
              maxHeight: "160px",
              display: "block",
              overflowY: "scroll",
            }}
            container
          >
            <Grid
              item
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              {filledTags.map((tag) => (
                <div
                  key={tag}
                  className="fs-standard"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: tagClass === "tag-azure" ? "#2e0054" : "#ddd",
                    color: tagClass === "tag-azure" ? "#fff" : "#777777",
                    border: tagClass === "tags-azure" ? "1px solid #2e0054" : "1px solid #777777",
                    borderRadius: "2px",
                    margin: "0 0.5rem 0.5rem 0",
                    padding: "0.25rem 0.75rem",
                  }}
                >
                  {tag}
                </div>
              ))}
            </Grid>
          </Grid>
        : <>
            <Grid container>
              {!hideTagsField && (
                <Grid
                  item
                  style={{
                    pointerEvents: "inherit",
                    maxHeight: "130px",
                    paddingRight: fullWidth ? 0 : "2rem",
                  }}
                  xs={fullWidth ? 12 : 11}
                >
                  {!readOnly &&
                    (showSuggestedTags && suggestions?.length ?
                      <ReactTags
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        renderLabel={() => <></>}
                        placeholderText={placeholder ? placeholder : "Add new tag"}
                        selected={filledTags.map((tag) => ({ label: tag, value: tag }))}
                        suggestions={suggestions}
                        onAdd={onAddTag}
                        onDelete={onDeleteTag}
                        onShouldExpand={(value) => (showSuggestionsOnFocus ? true : value.length > 1)}
                        suggestionsTransform={(query, suggestions) => {
                          const unselectedSuggestions = suggestions.filter(
                            (suggestion) => !filledTags.includes(suggestion.label),
                          );
                          if (!query.length) {
                            return unselectedSuggestions;
                          }

                          return unselectedSuggestions.filter((suggestedTag) =>
                            suggestedTag.label.toLowerCase().includes(query.toLowerCase()),
                          );
                        }}
                        renderTag={({ classNames, tag, ...tagProps }) => (
                          <button type="button" className={classNames.tag} {...tagProps}>
                            {tag.label}
                            <span className="ps-2">✕</span>
                          </button>
                        )}
                        allowNew={allowNew}
                        allowBackspace={true}
                        activateFirstOption
                        collapseOnSelect
                        isDisabled={disabled}
                      />
                    : <TagsInput
                        value={filledTags}
                        onChange={saveFilledTags}
                        addOnBlur={true}
                        tagProps={{
                          className: `react-tagsinput-tag tag-fill ${tagClass ? tagClass : "tag-azure"}`,
                        }}
                        inputProps={{
                          placeholder: placeholder,
                          disabled: inputDisabled,
                        }}
                        disabled={disabled}
                      />)}
                </Grid>
              )}
              {!hideEditButton && (
                <Grid item xs={1}>
                  <IconButton
                    onClick={() => showEditModal()}
                    style={
                      overrideButtonStyles ?
                        { ...overrideButtonStyles }
                      : {
                          display: hiddenTooltip ? "relative" : "inherit",
                          padding: smallIcon ? "6px" : "inherit",
                          marginLeft: "1rem",
                        }
                    }
                  >
                    <MdEdit />
                    {toolTip && hiddenTooltip && (
                      <HiddenToolTip
                        toolTip={toolTip}
                        id={toolTipId}
                        position={toolTipPosition ? toolTipPosition : "top"}
                      />
                    )}
                  </IconButton>
                </Grid>
              )}

              {altButton && (
                <span className={altButtonContainerClasses} onClick={() => showEditModal()}>
                  {altButton}
                </span>
              )}
            </Grid>
            <Modal
              isOpen={showEdit}
              onOpenChange={(isOpen) => !isOpen && closeEditModal()}
              title={modalTitle ?? name}
              className={css({ width: "60vw" })}
            >
              <VStack alignItems={"stretch"} paddingX="xlarge">
                <TextAreaField
                  aria-label="Mass input"
                  value={editTags}
                  onChange={(value) => {
                    setEditTags(value);
                  }}
                />
                <span>Separate text using commas or new lines</span>

                <Wrap justify="space-evenly">
                  <Button variant="secondary" onPress={() => closeEditModal()}>
                    Cancel
                  </Button>
                  <Button onPress={() => saveEditTags()}>Save</Button>
                </Wrap>
              </VStack>
            </Modal>
          </>
        }
      </div>;
};

export default Tags;
